// shared-lib/group-customization/physicalAxes.js
//
// The axes physics decides for a group member — the surface it stacks on, the
// wall plane it hangs on — as opposed to the ones its links or the user
// decide. The solver strips these from link implications and projects them
// after its pass (`resolveGroup`'s `physicalAxes`), so links never fight
// physics.
//
// Moved here from the web editor (`web-app/src/utils/groupCustomization.js`)
// so the server settles a group after a cabinet-run re-solve exactly as the
// browser does (`settleGroups.js`). The editor's drag-time X-center snap stays
// there; it reads `childTypeLike` from here.

const { isWallMounted, wallFaceDzInAnchorFrame } = require('./mountRules');

// Y-stack lookup: child.type → sibling type whose surface it rests against.
const STACK_TARGETS = {
  sink: 'countertop',
  'bathroom-sink-faucet': 'countertop',
  'bathroom-tub-faucet': 'tub-support-box',
};

// Sink mount → which side of countertop it rests against. 'top' = bottom of
// sink flush with countertop top (vessel). 'bottom' = top of sink flush with
// countertop bottom (undermount + dropin — dropin models include the rim
// that then protrudes above the countertop top).
const SINK_SIDE_BY_TYPE = {
  vessel: 'top',
  undermount: 'bottom',
  dropin: 'bottom',
  // pedestal / wall-mount → no stack snap (handled by wall-snap below)
};

// Category / synonym tolerant child-type test for the snap tables
// (#1535/L8): a `vanity-countertop` slab or a `double-sink` must engage the
// same snaps their canonical types do.
function childTypeLike(c, t) {
  if (c?.type === t) return true;
  if (c?.original?.category?.[t] === true) return true;
  if (t === 'countertop' && c?.type === 'vanity-countertop') return true;
  if (t === 'sink' && c?.type === 'double-sink') return true;
  return false;
}

function computeStackSnapDy(child, allChildren) {
  const targetType = STACK_TARGETS[child.type];
  if (!targetType) return null;
  const target = (allChildren || []).find((c) => childTypeLike(c, targetType));
  if (!target?.dimensions || target.dy == null) return null;
  let side;
  if (child.type === 'sink') {
    side = SINK_SIDE_BY_TYPE[child.original?.sinkType];
    if (!side) return null; // pedestal / wall-mount / unknown
  } else {
    // Faucet: wall-mount opts out; counter-mount sits on top
    const mt = child.original?.mountType;
    if (mt === 'wall' || mt === 'wall-mount') return null;
    side = 'top';
  }
  const childHalfH = (child.dimensions.y || 0) / 2;
  const targetHalfH = (target.dimensions.y || 0) / 2;
  return side === 'top'
    ? target.dy + targetHalfH + childHalfH
    : target.dy - targetHalfH - childHalfH;
}

function computeWallSnapDz(child, anchorObject, roomData) {
  if (!child?.dimensions || !anchorObject) return null;
  // Find the parent wall and project its room-side surface into anchor-local
  // coords. Falling back to the anchor's own back face is wrong when the
  // anchor sits with some offset from the wall — the user sees the
  // silhouette pushed slightly into the wall.
  const parentWallId = anchorObject.parentWallIds?.[0];
  const wall = roomData?.walls?.find((w) => w.id === parentWallId);
  const dz = wallFaceDzInAnchorFrame(wall, anchorObject, child.dimensions.z);
  if (dz != null) return dz;
  // Fallback: assume anchor's back face is the wall (legacy behavior).
  return -(anchorObject.dimensions?.z || 0) / 2 + (child.dimensions.z || 0) / 2;
}

/**
 * Axes whose value is physically determined for this child — set by the
 * wall plane (wall-mounted children) and/or the surface they stack on
 * (counter-mount sinks/faucets), not by the user or by links.
 *
 * Used by:
 *   - The constraint solver to (a) strip these axes from RP/centerLine/
 *     rail-y/linked-pair implications so links don't fight physics, and
 *     (b) project the values after BFS so every moving-set member ends
 *     up at its physically-determined pose.
 *   - The editor's `computeSnap` (which adds the X-center *suggestion* on
 *     top).
 */
function fixedAxes(child, anchorObject, allChildren, roomData) {
  if (!child?.type || !child.dimensions) return {};
  const out = {};
  const dy = computeStackSnapDy(child, allChildren);
  if (dy != null) out.dy = dy;
  if (isWallMounted(child)) {
    const dz = computeWallSnapDz(child, anchorObject, roomData);
    if (dz != null) out.dz = dz;
    // Wall-mounted children face into the room. The anchor's local +Z
    // already points away from its parent wall by convention, so a child
    // on the same wall matches the anchor's facing — rotYRel = 0. Covers
    // wall-mounted faucets, sconces, mirrors, and wall-mount sinks.
    out.rotYRel = 0;
  }
  return out;
}

module.exports = { childTypeLike, fixedAxes };
